// UserList.tsx - Fixed name search issue
import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Avatar,
  Stack,
  Tabs,
  Tab,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Menu,
} from '@mui/material';
import { 
  Refresh, 
  Search, 
  FilterList, 
  KeyboardArrowDown, 
  KeyboardArrowUp,
  Person,
  Email,
  Phone,
  CalendarToday,
  CheckCircle,
  Cancel,
  Close,
  Send,
  LocalOffer,
  Key,
  Download,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListUsersQuery,
  useSendCouponMutation,
} from '../../features/admin/userlistApi.ts';
import {
  useListCouponsQuery,
  Coupon,
} from '../../features/admin/couponApi.ts';

interface User {
  user_id: number;
  first_name: string;
  last_name: string;
  user_email: string;
  user_contact: string;
  otp: number | string;
  status: string;
  member_type: string;
  subscription_date: string | null;
  expiry_date: string | null;
}

interface UserCouponHistory {
  [userId: number]: number[];
}

// Helper function to normalize contact numbers for search
const normalizeContactNumber = (contact: string | undefined): string => {
  if (!contact) return '';
  return contact.replace(/\D/g, ''); // Remove all non-numeric characters
};

const UserList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState(1); 
  const [searchStr, setSearchStr] = useState('');
  const [filterType, setFilterType] = useState<number>(0);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [openSendCouponDialog, setOpenSendCouponDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedCoupon, setSelectedCoupon] = useState<number | ''>('');
  const [isSending, setIsSending] = useState(false);
  const [userCouponHistory, setUserCouponHistory] = useState<UserCouponHistory>({});
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(null);
  const [sendCoupon] = useSendCouponMutation();

  const { 
    data, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListUsersQuery({ filter_type: filterType });

  const {
    data: couponsData,
    isLoading: isLoadingCoupons,
  } = useListCouponsQuery();

  const users = data?.data || [];
  const allCoupons = couponsData?.data || [];
  const totalRecords = data?.total || users.length;
  
  const usersPerPage = 10;
  
  // FIX: CORRECT PAGINATION CALCULATION
  const filterCounts = useMemo(() => {
    const counts = {
      normal: 0,
      silver: 0,
      gold: 0,
      platinum: 0,
      all: 0
    };
    
    users.forEach((user: User) => {
      const memberType = user.member_type?.toLowerCase() || '';
      if (memberType === 'silver') {
        counts.silver++;
      } else if (memberType === 'gold') {
        counts.gold++;
      } else if (memberType === 'platinum') {
        counts.platinum++;
      } else {
        counts.normal++;
      }
    });
    
    counts.all = users.length;
    return counts;
  }, [users]);

  const filteredUsers = useMemo(() => {
    let filtered = users;
    
    // Apply membership filter first
    if (filterType === 0) {
      filtered = filtered.filter((user: User) => 
        !user.member_type || user.member_type.toLowerCase() === 'none'
      );
    } else if (filterType === 1) {
      filtered = filtered.filter((user: User) => 
        user.member_type?.toLowerCase() === 'silver'
      );
    } else if (filterType === 2) {
      filtered = filtered.filter((user: User) => 
        user.member_type?.toLowerCase() === 'gold'
      );
    } else if (filterType === 3) {
      filtered = filtered.filter((user: User) => 
        user.member_type?.toLowerCase() === 'platinum'
      );
    }
    
    // Apply search filter
    if (searchStr.trim()) {
      const searchTerm = searchStr.trim().toLowerCase();
      const normalizedSearchTerm = normalizeContactNumber(searchTerm);
      
      filtered = filtered.filter((user: User) => {
        const normalizedContact = normalizeContactNumber(user.user_contact);
        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().trim();
        
        // Check all searchable fields with better handling
        return (
          (user.first_name?.toLowerCase().includes(searchTerm) ?? false) ||
          (user.last_name?.toLowerCase().includes(searchTerm) ?? false) ||
          (fullName.includes(searchTerm)) ||
          (user.user_email?.toLowerCase().includes(searchTerm) ?? false) ||
          (normalizedContact.includes(normalizedSearchTerm) || user.user_contact?.includes(searchTerm)) ||
          (user.user_id?.toString().includes(searchTerm) ?? false) ||
          (user.otp?.toString().includes(searchTerm) ?? false)
        );
      });
    }
    
    return filtered;
  }, [users, filterType, searchStr]);

  const activeCoupons = useMemo(() => {
    const now = new Date();
    return allCoupons.filter((coupon: Coupon) => {
      const endDate = new Date(coupon.end_date);
      const isExpired = endDate < now;
      const isInactive = coupon.status === '1';
      return !isExpired && !isInactive;
    });
  }, [allCoupons]);

  // FIX: Use filteredUsers.length for pagination calculation
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / usersPerPage));
  
  // Ensure page doesn't exceed totalPages and reset to page 1 if needed
  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [filteredUsers, totalPages, page]);
  
  const currentPage = Math.min(page, totalPages);
  
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  // CSV Download Functions
  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const downloadCSV = (usersToDownload: User[], filename: string) => {
    const headers = [
      'User ID',
      'First Name',
      'Last Name',
      'Email',
      'Contact',
      'OTP',
      'Status',
      'Member Type',
      'Subscription Date',
      'Expiry Date'
    ];

    const csvRows = [
      headers.join(','),
      ...usersToDownload.map(user => [
        user.user_id,
        `"${user.first_name || ''}"`,
        `"${user.last_name || ''}"`,
        `"${user.user_email || ''}"`,
        `"${user.user_contact || ''}"`,
        user.otp || '',
        user.status || '',
        user.member_type || 'None',
        user.subscription_date || '',
        user.expiry_date || ''
      ].join(','))
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSnackbar({
      open: true,
      message: `CSV downloaded successfully!`,
      severity: 'success'
    });
  };

  const downloadAllUsersCSV = () => {
    downloadCSV(users, `all_users_${new Date().toISOString().split('T')[0]}.csv`);
    handleDownloadClose();
  };

  const downloadFilteredUsersCSV = () => {
    const filterName = filterType === 0 ? 'normal' : 
                      filterType === 1 ? 'silver' : 
                      filterType === 2 ? 'gold' : 'platinum';
    downloadCSV(filteredUsers, `${filterName}_users_${new Date().toISOString().split('T')[0]}.csv`);
    handleDownloadClose();
  };

  const downloadCurrentPageCSV = () => {
    downloadCSV(paginatedUsers, `users_page_${currentPage}_${new Date().toISOString().split('T')[0]}.csv`);
    handleDownloadClose();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchStr(value);
    setPage(1); // Reset to first page when searching
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      setPage(1); // Reset to first page
      setSnackbar({
        open: true,
        message: 'Users refreshed successfully!',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Error refreshing users',
        severity: 'error'
      });
    }
  };

  const handleToggleExpand = (userId: number) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleFilterChange = (newFilter: number) => {
    setFilterType(newFilter);
    setPage(1); // Reset to first page when changing filter
  };

  const handleOpenSendCouponDialog = () => {
    setOpenSendCouponDialog(true);
    setSelectedUsers([]);
    setSelectedCoupon('');
    setUserCouponHistory({});
  };

  const handleCloseSendCouponDialog = () => {
    setOpenSendCouponDialog(false);
    setSelectedUsers([]);
    setSelectedCoupon('');
    setUserCouponHistory({});
  };

  const handleUserSelection = (userId: number) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map((user: User) => user.user_id));
    }
  };

  const handleSendCoupon = async () => {
    if (!selectedCoupon || selectedUsers.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select a coupon and at least one user',
        severity: 'error',
      });
      return;
    }

    setIsSending(true);

    let successCount = 0;
    let alreadySentCount = 0;
    let failedCount = 0;

    try {
      for (const userId of selectedUsers) {
        try {
          const res = await sendCoupon({
            coupon_id: Number(selectedCoupon),
            user_id: userId,
          }).unwrap();

          if (res.sent_users === 1) {
            successCount++;
            setUserCouponHistory((prev) => ({
              ...prev,
              [userId]: [...(prev[userId] || []), Number(selectedCoupon)],
            }));
          } else if (res.already_sent_users === 1) {
            alreadySentCount++;
            setUserCouponHistory((prev) => ({
              ...prev,
              [userId]: [...(prev[userId] || []), Number(selectedCoupon)],
            }));
          } else {
            failedCount++;
          }
        } catch {
          failedCount++;
        }
      }

      if (successCount > 0 && alreadySentCount === 0) {
        setSnackbar({
          open: true,
          message: `Coupon sent successfully to ${successCount} user${successCount > 1 ? 's' : ''}!`,
          severity: 'success',
        });
        handleCloseSendCouponDialog();
      } 
      else if (successCount > 0 && alreadySentCount > 0) {
        setSnackbar({
          open: true,
          message: `Coupon sent to ${successCount} user${successCount > 1 ? 's' : ''}. ${alreadySentCount} user${alreadySentCount > 1 ? 's' : ''} already received this coupon.`,
          severity: 'warning',
        });
      } 
      else if (successCount === 0 && alreadySentCount > 0) {
        setSnackbar({
          open: true,
          message: 'This coupon is already sent to the selected user(s).',
          severity: 'error',
        });
      } 
      else {
        setSnackbar({
          open: true,
          message: 'Failed to send coupon. Please try again.',
          severity: 'error',
        });
      }
    } finally {
      setIsSending(false);
    }
  };

  const hasUserReceivedCoupon = (userId: number) => {
    return userCouponHistory[userId]?.includes(Number(selectedCoupon)) || false;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getMemberTypeColor = (memberType: string) => {
    switch (memberType?.toLowerCase()) {
      case 'silver':
        return { bgcolor: '#e8eaf6', color: '#3f51b5' };
      case 'gold':
        return { bgcolor: '#fff8e1', color: '#f57c00' };
      case 'platinum':
        return { bgcolor: '#f3e5f5', color: '#7b1fa2' };
      case 'none':
      default:
        return { bgcolor: '#f5f5f5', color: '#616161' };
    }
  };

  const getStatusColor = (status: string) => {
    return status?.toLowerCase() === 'active' 
      ? { bgcolor: '#d1f4e0', color: '#00783e' }
      : { bgcolor: '#ffebee', color: '#c62828' };
  };

  const clearFilters = () => {
    setSearchStr('');
    setFilterType(0);
    setPage(1);
  };

  const getCouponDisplayText = (coupon: Coupon) => {
    return `${coupon.code} - ${coupon.discount_type === 'flat' ? `AED ${coupon.discount_value}` : `${coupon.discount_value}%`}`;
  };

  const formatOTP = (otp: number | string) => {
    if (!otp && otp !== 0) return '-';
    return otp.toString();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                  User Management
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Refresh
                  </Button>
                  
                  {/* Download Button */}
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleDownloadClick}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Download CSV
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<Send />}
                    onClick={handleOpenSendCouponDialog}
                    sx={{
                      bgcolor: '#008060',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#006e52' }
                    }}
                  >
                    Send Coupon
                  </Button>
                </Box>
              </Box>

              {/* Download Menu */}
              <Menu
                anchorEl={downloadAnchorEl}
                open={Boolean(downloadAnchorEl)}
                onClose={handleDownloadClose}
                PaperProps={{
                  sx: {
                    borderRadius: '8px',
                    mt: 1,
                    minWidth: 220,
                  }
                }}
              >
                <MenuItem onClick={downloadAllUsersCSV}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" fontWeight={500}>
                      All Users ({filterCounts.all})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Download complete user list
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem onClick={downloadFilteredUsersCSV}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" fontWeight={500}>
                      Current Filter (
                      {filterType === 0 ? 'Normal' : 
                       filterType === 1 ? 'Silver' : 
                       filterType === 2 ? 'Gold' : 'Platinum'}) 
                      ({filteredUsers.length})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Download users with current filter
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem onClick={downloadCurrentPageCSV}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" fontWeight={500}>
                      Current Page ({paginatedUsers.length})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Download users on page {currentPage}
                    </Typography>
                  </Box>
                </MenuItem>
              </Menu>

              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs 
                    value={filterType} 
                    onChange={(e, v) => handleFilterChange(v)}
                    sx={{
                      px: 2,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '14px',
                        minHeight: 48,
                        color: '#6d7175',
                      },
                      '& .Mui-selected': {
                        color: '#202223',
                        fontWeight: 600,
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#202223',
                        height: 3,
                      }
                    }}
                  >
                    <Tab label={`Normal (${filterCounts.normal})`} value={0} />
                    <Tab label={`Silver (${filterCounts.silver})`} value={1} />
                    <Tab label={`Gold (${filterCounts.gold})`} value={2} />
                    <Tab label={`Platinum (${filterCounts.platinum})`} value={3} />
                  </Tabs>
                </Box>

                <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search users by name, email, contact, ID or OTP..."
                    value={searchStr}
                    onChange={handleSearch}
                    fullWidth
                    sx={{ 
                      maxWidth: 400,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        bgcolor: 'white',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: '#6d7175' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    onClick={clearFilters}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Clear
                  </Button>
                </Box>

                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {isError && !isLoading && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">Failed to load users. Please try again.</Alert>
                  </Box>
                )}

                {!isLoading && !isError && paginatedUsers.length === 0 && ( 
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      No users found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {searchStr ? `No results for "${searchStr}"` : 'Users will appear here when they register'}
                    </Typography>
                    {searchStr && (
                      <Button
                        variant="outlined"
                        onClick={clearFilters}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#c9cccf',
                          color: '#202223',
                          borderRadius: '8px',
                          fontWeight: 500,
                        }}
                      >
                        Clear Search
                      </Button>
                    )}
                  </Box>
                )}

                {!isLoading && paginatedUsers.length > 0 && (
                  <>
                    {/* Show search results count */}
                    {searchStr && (
                      <Box sx={{ px: 2, py: 1.5, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                        <Typography variant="body2" color="text.secondary">
                          Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} for "{searchStr}"
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ bgcolor: 'white' }}>
                      {!isMobile && (
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: '50px 80px 1fr 140px 200px 140px 100px 120px 120px 120px',
                          gap: 2,
                          px: 2,
                          py: 1.5,
                          borderBottom: '1px solid #e1e3e5',
                          bgcolor: '#fafbfb'
                        }}>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            ID
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            User
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            OTP
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Email
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Contact
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Status
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Membership
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Subscribed
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                            Expires
                          </Typography>
                        </Box>
                      )}

                      {paginatedUsers.map((user: User) => (
                        <Box key={user.user_id}>
                          <Box
                            sx={!isMobile ? {
                              display: 'grid',
                              gridTemplateColumns: '50px 80px 1fr 140px 200px 140px 100px 120px 120px 120px',
                              gap: 2,
                              px: 2,
                              py: 2,
                              alignItems: 'center',
                              borderBottom: '1px solid #e1e3e5',
                              '&:hover': { bgcolor: '#f9fafb' },
                              transition: 'background-color 0.15s ease',
                              cursor: 'pointer'
                            } : {
                              p: 2,
                              borderBottom: '1px solid #e1e3e5',
                              '&:hover': { bgcolor: '#f9fafb' },
                              cursor: 'pointer'
                            }}
                            onClick={() => handleToggleExpand(user.user_id)}
                          >
                            {!isMobile ? (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleExpand(user.user_id);
                                  }}
                                  sx={{ color: '#6d7175' }}
                                >
                                  {expandedUserId === user.user_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>

                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                  #{user.user_id}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar
                                    sx={{ 
                                      width: 32, 
                                      height: 32, 
                                      bgcolor: '#f1f2f4',
                                      color: '#202223',
                                      fontSize: '14px',
                                      fontWeight: 600
                                    }}
                                  >
                                    {(user.first_name || 'U')[0].toUpperCase()}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                      {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                                    </Typography>
                                  </Box>
                                </Box>

                                <Tooltip title="OTP" arrow>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Key sx={{ fontSize: 14, color: '#6d7175' }} />
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontWeight: 600,
                                        fontFamily: 'monospace',
                                        color: '#202223',
                                        fontSize: '13px'
                                      }}
                                    >
                                      {formatOTP(user.otp)}
                                    </Typography>
                                  </Box>
                                </Tooltip>

                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: '12px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={user.user_email}
                                >
                                  {user.user_email || '-'}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                  {user.user_contact || '-'}
                                </Typography>

                                <Chip 
                                  label={user.status || 'Unknown'} 
                                  size="small"
                                  icon={user.status?.toLowerCase() === 'active' ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
                                  sx={{ 
                                    ...getStatusColor(user.status),
                                    fontWeight: 600,
                                    fontSize: '11px'
                                  }}
                                />

                                <Chip 
                                  label={user.member_type || 'None'} 
                                  size="small"
                                  sx={{ 
                                    ...getMemberTypeColor(user.member_type),
                                    fontWeight: 600,
                                    fontSize: '11px'
                                  }}
                                />

                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                  {formatDate(user.subscription_date)}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                  {formatDate(user.expiry_date)}
                                </Typography>
                              </>
                            ) : (
                              <Stack spacing={2}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar
                                      sx={{ 
                                        width: 40, 
                                        height: 40, 
                                        bgcolor: '#f1f2f4',
                                        color: '#202223',
                                        fontWeight: 600
                                      }}
                                    >
                                      {(user.first_name || 'U')[0].toUpperCase()}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                        {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        ID: {user.user_id}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleExpand(user.user_id);
                                    }}
                                    sx={{ color: '#6d7175' }}
                                  >
                                    {expandedUserId === user.user_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                  </IconButton>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Key sx={{ fontSize: 16, color: '#6d7175' }} />
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                    OTP: {formatOTP(user.otp)}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  <Chip 
                                    label={user.status || 'Unknown'} 
                                    size="small"
                                    icon={user.status?.toLowerCase() === 'active' ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
                                    sx={{ 
                                      ...getStatusColor(user.status),
                                      fontWeight: 600
                                    }}
                                  />
                                  <Chip 
                                    label={user.member_type || 'None'} 
                                    size="small"
                                    sx={{ 
                                      ...getMemberTypeColor(user.member_type),
                                      fontWeight: 600
                                    }}
                                  />
                                </Box>
                              </Stack>
                            )}
                          </Box>

                          <Collapse in={expandedUserId === user.user_id} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 3, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                                User Details
                              </Typography>
                              
                              <Stack spacing={2}>
                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Key sx={{ color: '#6d7175' }} />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">
                                        OTP
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: '#202223' }}>
                                        {formatOTP(user.otp)}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Email sx={{ color: '#6d7175' }} />
                                    <Box>
                                      <Typography variant="caption" color="text-secondary">
                                        Email Address
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                                        {user.user_email || '-'}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Phone sx={{ color: '#6d7175' }} />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">
                                        Contact Number
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                                        {user.user_contact || '-'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Card>

                                {(user.subscription_date || user.expiry_date) && (
                                  <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                      <CalendarToday sx={{ color: '#6d7175' }} />
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          Subscription Date
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {formatDate(user.subscription_date)}
                                        </Typography>
                                      </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      <CalendarToday sx={{ color: '#6d7175' }} />
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          Expiry Date
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {formatDate(user.expiry_date)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Card>
                                )}
                              </Stack>
                            </Box>
                          </Collapse>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Card>

              {/* FIX: Show pagination only when there's more than 1 page */}
              {totalPages > 1 && !isLoading && filteredUsers.length > usersPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handleChangePage}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: '8px',
                        fontWeight: 500,
                      },
                      '& .Mui-selected': {
                        bgcolor: '#202223 !important',
                        color: 'white',
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Send Coupon Dialog */}
      <Dialog
        open={openSendCouponDialog}
        onClose={handleCloseSendCouponDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
              Send coupon to Users
            </Typography>
            <IconButton onClick={handleCloseSendCouponDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Select Coupon *</InputLabel>
              <Select
                value={selectedCoupon}
                label="Select Coupon *" 
                onChange={(e) => {
                  setSelectedCoupon(e.target.value as number);
                  setUserCouponHistory({});
                }}
                disabled={isLoadingCoupons || activeCoupons.length === 0}
              >
                {activeCoupons.map((coupon: Coupon) => (
                  <MenuItem key={coupon.id} value={coupon.id}>
                    {getCouponDisplayText(coupon)}
                  </MenuItem>
                ))}
              </Select>
              {isLoadingCoupons && (
                <Typography variant="caption" color="text.secondary">
                  Loading Coupons...
                </Typography>
              )}
              {!isLoadingCoupons && activeCoupons.length === 0 && (
                <Typography variant="caption" color="error">
                  No active coupons available. Please create a coupon first.
                </Typography>
              )}
            </FormControl>

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Select Users ({selectedUsers.length} selected)
                </Typography>
                <Button
                  onClick={handleSelectAllUsers}
                  sx={{ textTransform: 'none' }}
                >
                  {selectedUsers.length === filteredUsers.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>

              {selectedCoupon && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Note: Users can only receive this coupon once. Users who have already received this coupon will be skipped.
                </Alert>
              )}

              <Card sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e1e3e5' }}>
                <List>
                  {filteredUsers.map((user: User) => {
                    const hasReceived = hasUserReceivedCoupon(user.user_id);
                    
                    return (
                      <ListItem
                        key={user.user_id}
                        dense
                        button
                        onClick={() => !hasReceived && handleUserSelection(user.user_id)}
                        disabled={hasReceived}
                        sx={{
                          opacity: hasReceived ? 0.6 : 1,
                          '&.Mui-disabled': {
                            pointerEvents: 'auto',
                            cursor: 'not-allowed',
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedUsers.includes(user.user_id)}
                            tabIndex={-1}
                            disableRipple
                            disabled={hasReceived}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                              </Typography>
                              {hasReceived && (
                                <Chip 
                                  label="Already Sent" 
                                  size="small"
                                  sx={{ 
                                    bgcolor: '#ffebee', 
                                    color: '#c62828',
                                    fontWeight: 600,
                                    fontSize: '10px'
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {user.user_email}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                ID: {user.user_id} | OTP: {formatOTP(user.otp)} | {user.user_contact}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Chip 
                            label={user.member_type || 'None'} 
                            size="small"
                            sx={{ 
                              ...getMemberTypeColor(user.member_type),
                              fontWeight: 600,
                              fontSize: '10px'
                            }}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </Card>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseSendCouponDialog}
            disabled={isSending}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              color: '#202223',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSendCoupon}
            variant="contained"
            disabled={isSending || !selectedCoupon || selectedUsers.length === 0}
            startIcon={<Send />}
            sx={{
              bgcolor: '#008060',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#006e52' }
            }}
          >
            {isSending ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                 Sending...
              </Box>
            ) : (
              `Send to ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserList;